import { supabase } from '../config/supabase.js';
import { ApiUtils } from './api-utils.js';
export class MedicationTrackingService {
    /**
     * Get standardized frequency options for providers to use
     * These are the recommended frequency formats that work best with the calculation logic
     */
    getStandardFrequencyOptions() {
        return [
            { value: '1', label: 'Daily', description: 'Every day' },
            { value: '2', label: 'Every Other Day', description: 'Every 2 days' },
            { value: '3', label: 'Every Third Day', description: 'Every 3 days' },
            { value: '7', label: 'Weekly', description: 'Every 7 days' },
            { value: '14', label: 'Bi-weekly', description: 'Every 14 days' },
            { value: '30', label: 'Monthly', description: 'Every 30 days' },
            // Common medical frequencies
            { value: 'twice daily', label: 'Twice Daily (BID)', description: 'Every 12 hours' },
            { value: 'three times daily', label: 'Three Times Daily (TID)', description: 'Every 8 hours' },
            { value: 'four times daily', label: 'Four Times Daily (QID)', description: 'Every 6 hours' },
            { value: 'twice weekly', label: 'Twice Weekly', description: 'Every 3-4 days' },
            { value: 'three times weekly', label: 'Three Times Weekly', description: 'Every 2-3 days' },
            // Custom options
            { value: 'custom', label: 'Custom (specify days)', description: 'Enter number of days between doses' }
        ];
    }
    /**
     * Validate and normalize frequency input from providers
     * Converts various formats to a standardized format for calculation
     */
    normalizeFrequency(input) {
        if (!input) {
            return { normalized: '7', daysInterval: 7, isValid: false };
        }
        const freq = input.toLowerCase().trim();
        // Check if it's a simple number (days)
        const simpleNumber = /^\d+$/.test(input.trim());
        if (simpleNumber) {
            const days = parseInt(input.trim());
            return {
                normalized: days.toString(),
                daysInterval: days,
                isValid: days > 0 && days <= 365
            };
        }
        // Test the frequency with our calculation logic to ensure it works
        try {
            const testDate = '2024-01-01';
            const nextDate = this.calculateNextDoseDate(testDate, input);
            const testLastDate = new Date(testDate);
            const testNextDate = new Date(nextDate);
            const daysDiff = Math.round((testNextDate.getTime() - testLastDate.getTime()) / (1000 * 60 * 60 * 24));
            return {
                normalized: input,
                daysInterval: daysDiff,
                isValid: daysDiff > 0 && daysDiff <= 365
            };
        }
        catch (error) {
            return { normalized: '7', daysInterval: 7, isValid: false };
        }
    }
    /**
     * Calculate next dose date based on frequency
     *
     * Supports multiple frequency formats:
     * - Numbers: "1", "2", "7", "14", "30" (days between doses)
     * - Text: "daily", "every other day", "weekly", "monthly"
     * - Medical: "twice daily", "three times daily", "bid", "tid", "qid"
     * - Complex: "every 2 days", "every other day", "twice weekly"
     */
    calculateNextDoseDate(lastTakenDate, frequency) {
        const lastDate = new Date(lastTakenDate);
        // Parse frequency string and add appropriate days
        let daysToAdd = 7; // Default to weekly
        if (frequency) {
            const freq = frequency.toLowerCase().trim();
            // Handle special text patterns first (most specific)
            if (freq.includes('every other day') || freq.includes('every 2nd day') || freq.includes('alternate day')) {
                daysToAdd = 2;
            }
            else if (freq.includes('every third day') || freq.includes('every 3rd day')) {
                daysToAdd = 3;
            }
            else if (freq.includes('twice daily') || freq.includes('2x daily') || freq.includes('bid')) {
                daysToAdd = 0.5; // Every 12 hours
            }
            else if (freq.includes('three times daily') || freq.includes('3x daily') || freq.includes('tid')) {
                daysToAdd = 0.33; // Every 8 hours
            }
            else if (freq.includes('four times daily') || freq.includes('4x daily') || freq.includes('qid')) {
                daysToAdd = 0.25; // Every 6 hours
            }
            else if (freq.includes('twice weekly') || freq.includes('2x weekly') || freq.includes('biweekly')) {
                daysToAdd = 3.5; // Approximately every 3-4 days
            }
            else if (freq.includes('three times weekly') || freq.includes('3x weekly')) {
                daysToAdd = 2.33; // Approximately every 2-3 days
            }
            else if (freq.includes('daily') || freq.includes('once daily') || freq.includes('1x daily') || freq.includes('qd')) {
                daysToAdd = 1;
            }
            else if (freq.includes('weekly') || freq.includes('once weekly') || freq.includes('1x weekly') || freq.includes('week')) {
                daysToAdd = 7;
            }
            else if (freq.includes('bimonthly') || freq.includes('twice monthly') || freq.includes('2x monthly')) {
                daysToAdd = 15;
            }
            else if (freq.includes('monthly') || freq.includes('once monthly') || freq.includes('1x monthly') || freq.includes('month')) {
                daysToAdd = 30;
            }
            else {
                // Handle numeric patterns (fallback)
                const numericPatterns = [
                    // "Every X days" patterns
                    /every\s+(\d+)\s+days?/,
                    // Simple number patterns
                    /^(\d+)\s*$/,
                    /^(\d+)\s+days?$/,
                    // "X-day" patterns
                    /(\d+)-day/,
                ];
                for (const pattern of numericPatterns) {
                    const match = freq.match(pattern);
                    if (match) {
                        daysToAdd = parseInt(match[1]);
                        break;
                    }
                }
            }
            console.log('🔍 Frequency parsing:', {
                originalFrequency: frequency,
                normalizedFreq: freq,
                daysToAdd
            });
        }
        const nextDate = new Date(lastDate);
        nextDate.setDate(nextDate.getDate() + Math.ceil(daysToAdd));
        return nextDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    }
    /**
     * Get next dose information for a medication
     */
    async getNextDoseInfo(medicationPreferenceId, frequency) {
        try {
            const latestEntryResult = await this.getLatestTrackingEntry(medicationPreferenceId);
            if (!latestEntryResult.success || !latestEntryResult.data) {
                // No previous doses, return null
                return ApiUtils.createResponse(true, {
                    nextDueDate: null,
                    daysSinceLastDose: null
                });
            }
            const lastEntry = latestEntryResult.data;
            const lastTakenDate = lastEntry.taken_date;
            const nextDueDate = this.calculateNextDoseDate(lastTakenDate, frequency);
            // Calculate days since last dose
            const today = new Date();
            const lastDate = new Date(lastTakenDate);
            const daysSinceLastDose = Math.floor((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
            return ApiUtils.createResponse(true, {
                nextDueDate,
                daysSinceLastDose
            });
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, { nextDueDate: null, daysSinceLastDose: null }, errorMsg, code);
        }
    }
    /**
     * Create a new medication tracking entry
     */
    async createTrackingEntry(data) {
        try {
            // Get current patient_id from auth
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                return ApiUtils.createResponse(false, null, 'User not authenticated', 'AUTH_ERROR');
            }
            // Get patient record
            const { data: patient } = await supabase
                .from('patients')
                .select('id')
                .eq('profile_id', user.id)
                .single();
            if (!patient) {
                return ApiUtils.createResponse(false, null, 'Patient profile not found', 'NOT_FOUND');
            }
            const { data: entry, error } = await supabase
                .from('medication_tracking_entries')
                .insert({
                patient_id: patient.id,
                ...data
            })
                .select()
                .single();
            if (error) {
                if (error.code === '23505') {
                    return ApiUtils.createResponse(false, null, 'Entry already exists for this medication on this date', 'DUPLICATE_ENTRY');
                }
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, entry);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Update an existing medication tracking entry
     */
    async updateTrackingEntry(entryId, data) {
        try {
            const { data: entry, error } = await supabase
                .from('medication_tracking_entries')
                .update({
                ...data,
                updated_at: new Date().toISOString()
            })
                .eq('id', entryId)
                .select()
                .single();
            if (error) {
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, entry);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Delete a medication tracking entry
     */
    async deleteTrackingEntry(entryId) {
        try {
            const { error } = await supabase
                .from('medication_tracking_entries')
                .delete()
                .eq('id', entryId);
            if (error) {
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, undefined);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Get tracking entries for a specific medication preference
     */
    async getTrackingEntriesByMedication(medicationPreferenceId) {
        try {
            const { data: entries, error } = await supabase
                .from('medication_tracking_entries')
                .select('*')
                .eq('medication_preference_id', medicationPreferenceId)
                .order('taken_date', { ascending: false });
            if (error) {
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, entries || []);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Get all tracking entries for the current patient using direct fetch API
     */
    async getPatientTrackingEntries() {
        try {
            console.log('🔍 MedicationTrackingService: Getting patient tracking entries with direct fetch API');
            // Get current patient_id from auth
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                return ApiUtils.createResponse(false, null, 'User not authenticated', 'AUTH_ERROR');
            }
            // Get patient record
            const { data: patient } = await supabase
                .from('patients')
                .select('id')
                .eq('profile_id', user.id)
                .single();
            if (!patient) {
                return ApiUtils.createResponse(false, null, 'Patient profile not found', 'NOT_FOUND');
            }
            console.log('🔍 MedicationTrackingService: Using patient ID:', patient.id);
            // Use environment-aware configuration instead of hardcoded local URL
            const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js');
            const baseUrl = getEnvironmentAwareSupabaseUrl();
            const serviceKey = getEnvironmentAwareSupabaseServiceKey();
            const apiUrl = `${baseUrl}/rest/v1/medication_tracking_entries`;
            const headers = {
                'apikey': serviceKey,
                'Authorization': `Bearer ${serviceKey}`,
                'Content-Type': 'application/json'
            };
            const queryParams = new URLSearchParams({
                'patient_id': `eq.${patient.id}`,
                'select': `*`,
                'order': 'taken_date.asc'
            });
            console.log('🔍 MedicationTrackingService: Executing direct fetch API call');
            console.log('🔍 API URL:', `${apiUrl}?${queryParams.toString()}`);
            const response = await fetch(`${apiUrl}?${queryParams.toString()}`, {
                method: 'GET',
                headers: headers
            });
            console.log('🔍 MedicationTrackingService: Fetch response status:', response.status, response.statusText);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ MedicationTrackingService: Fetch error:', errorText);
                return ApiUtils.createResponse(false, null, `HTTP ${response.status}: ${errorText}`, 'FETCH_ERROR');
            }
            const rawEntries = await response.json();
            console.log('✅ MedicationTrackingService: Fetch success. Raw data count:', rawEntries?.length);
            console.log('✅ MedicationTrackingService: Raw data sample:', rawEntries?.slice(0, 3));
            // If we have entries, manually fetch medication preference data
            if (rawEntries && rawEntries.length > 0) {
                console.log('🔍 MedicationTrackingService: Enriching entries with medication preference data');
                // Get unique medication preference IDs
                const preferenceIds = [...new Set(rawEntries.map((entry) => entry.medication_preference_id))];
                console.log('🔍 MedicationTrackingService: Found preference IDs:', preferenceIds);
                // Fetch medication preferences and medications in one query
                const prefsUrl = `${baseUrl}/rest/v1/patient_medication_preferences`;
                const prefsQuery = new URLSearchParams({
                    'id': `in.(${preferenceIds.join(',')})`,
                    'select': 'id,preferred_dosage,medications(name)'
                });
                const prefsResponse = await fetch(`${prefsUrl}?${prefsQuery.toString()}`, {
                    method: 'GET',
                    headers: headers
                });
                if (prefsResponse.ok) {
                    const preferences = await prefsResponse.json();
                    console.log('✅ MedicationTrackingService: Fetched preferences:', preferences);
                    // Create a map for quick lookup
                    const prefMap = new Map(preferences.map((pref) => [pref.id, pref]));
                    // Enrich tracking entries with medication data
                    const enrichedEntries = rawEntries.map((entry) => ({
                        ...entry,
                        patient_medication_preferences: prefMap.get(entry.medication_preference_id) || null
                    }));
                    console.log('✅ MedicationTrackingService: Enriched entries:', enrichedEntries.slice(0, 2));
                    return ApiUtils.createResponse(true, enrichedEntries);
                }
                else {
                    console.log('⚠️ MedicationTrackingService: Failed to fetch preferences, returning entries without enrichment');
                }
            }
            return ApiUtils.createResponse(true, rawEntries || []);
        }
        catch (error) {
            console.error('❌ MedicationTrackingService: Direct fetch error:', error);
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Get the latest tracking entry for a specific medication
     */
    async getLatestTrackingEntry(medicationPreferenceId) {
        try {
            const { data: entry, error } = await supabase
                .from('medication_tracking_entries')
                .select('*')
                .eq('medication_preference_id', medicationPreferenceId)
                .order('taken_date', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (error) {
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, entry);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
    /**
     * Check if a tracking entry exists for a specific medication on a specific date
     */
    async hasTrackingEntryForDate(medicationPreferenceId, date) {
        try {
            const { data: entry, error } = await supabase
                .from('medication_tracking_entries')
                .select('id')
                .eq('medication_preference_id', medicationPreferenceId)
                .eq('taken_date', date)
                .maybeSingle();
            if (error) {
                const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
                return ApiUtils.createResponse(false, null, errorMsg, code);
            }
            return ApiUtils.createResponse(true, !!entry);
        }
        catch (error) {
            const { error: errorMsg, code } = ApiUtils.handleSupabaseError(error);
            return ApiUtils.createResponse(false, null, errorMsg, code);
        }
    }
}
// Export singleton instance
export const medicationTrackingService = new MedicationTrackingService();
